/**
 * Sync Manager - Intelligent Auto-Sync System
 *
 * Orchestrates automatic synchronization between devices.
 * - Sync on login (fullSync)
 * - Sync on tab focus (pull)
 * - Sync after data mutation (debounced push)
 *
 * Auto-sync is enabled for ALL users by default.
 * Users can toggle it off in Settings.
 *
 * See SYNC_PLAN.md for architecture details.
 */

import { pushAll, pull, fullSync, checkConnection, getLastSyncAt } from './sync';

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'ninjacash-sync-settings';

// =============================================================================
// Types
// =============================================================================

export interface SyncSettings {
  // Master switch
  autoSyncEnabled: boolean;

  // Trigger controls
  syncOnLogin: boolean;
  syncOnFocus: boolean;
  syncOnMutation: boolean;

  // Timing (milliseconds)
  pushDebounceMs: number;
  pullIntervalMs: number; // 0 = disabled

  // Conflict resolution
  conflictStrategy: 'server-wins' | 'client-wins' | 'last-write-wins';
}

export interface SyncStatus {
  isSyncing: boolean;
  lastSyncAt: Date | null;
  pendingChanges: number;
  lastError: string | null;
  connectionStatus: 'connected' | 'disconnected' | 'checking';
  autoSyncEnabled: boolean;
}

type SyncStatusListener = (status: SyncStatus) => void;

// =============================================================================
// Default Settings
// =============================================================================

const DEFAULT_SYNC_SETTINGS: SyncSettings = {
  autoSyncEnabled: true,
  syncOnLogin: true,
  syncOnFocus: true,
  syncOnMutation: true,
  pushDebounceMs: 9000, // 9 seconds - responsive for cross-device, won't spam server
  pullIntervalMs: 0, // Disabled by default
  conflictStrategy: 'last-write-wins',
};

// =============================================================================
// Sync Manager Class
// =============================================================================

class SyncManager {
  private settings: SyncSettings;
  private userId: string | null = null;
  private userEmail: string | null = null;
  private hasSyncAccess = false; // BUG-065 FIX: Defense in depth - only Pro/Trial can sync
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  private pullInterval: ReturnType<typeof setInterval> | null = null;
  private listeners: Set<SyncStatusListener> = new Set();
  private isSyncing = false;
  private pendingChanges = 0;
  private lastError: string | null = null;
  private connectionStatus: 'connected' | 'disconnected' | 'checking' = 'checking';
  private initialized = false;

  // BUG-061 FIX: Mutex and cooldown to prevent sync racing
  private fullSyncLock = false; // True while a full sync is in progress or queued
  private syncQueuedAfterCurrent = false; // If true, run one more sync after current completes
  private lastSyncCompletedAt = 0; // Timestamp of last sync completion (for cooldown)
  private static readonly SYNC_COOLDOWN_MS = 5000; // Skip sync if completed within this window

  constructor() {
    this.settings = this.loadSettings();
  }

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  /**
   * Initialize sync manager with user context.
   * Call this after user logs in AND has sync access (Pro/Trial/Lifetime).
   *
   * SAFETY: If already initialized, cleanup first to prevent event listener leaks.
   * BUG-065 FIX: hasSyncAccess flag provides defense-in-depth against race conditions.
   */
  init(userId: string | null, userEmail: string | null, hasSyncAccess: boolean = false): void {
    // Prevent event listener accumulation from duplicate init() calls
    if (this.initialized) {
      console.log('[SYNC] Already initialized - cleaning up first');
      this.cleanup();
    }

    this.userId = userId;
    this.userEmail = userEmail;
    this.hasSyncAccess = hasSyncAccess; // BUG-065 FIX: Store sync access flag

    if (!userId) {
      console.log('[SYNC] Not initialized - no user');
      return;
    }

    // BUG-065 FIX: Defense in depth - don't enable sync for free users
    if (!hasSyncAccess) {
      console.log('[SYNC] Not initialized - user does not have sync access (free user)');
      return;
    }

    // Check if auto-sync is enabled (user preference from Settings)
    if (!this.shouldEnableAutoSync()) {
      console.log('[SYNC] Auto-sync disabled by user preference');
      this.notifyListeners();
      return;
    }

    // Setup event listeners
    if (this.settings.syncOnFocus) {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('focus', this.handleFocus);
    }

    // Setup periodic pull (if configured)
    if (this.settings.pullIntervalMs > 0) {
      this.pullInterval = setInterval(() => {
        this.triggerPull();
      }, this.settings.pullIntervalMs);
    }

    // Check connection
    this.checkConnectionStatus();

    this.initialized = true;
    console.log('[SYNC] Manager initialized for:', userEmail, this.settings);
    this.notifyListeners();
  }

  /**
   * Check if auto-sync should be enabled for current user.
   * Returns the user's saved preference (default: true).
   */
  private shouldEnableAutoSync(): boolean {
    // All logged-in users can use sync
    // Respect the user's saved setting (from localStorage)
    return this.settings.autoSyncEnabled;
  }

  /**
   * Cleanup on logout.
   */
  cleanup(): void {
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
      this.pushTimer = null;
    }
    if (this.pullInterval) {
      clearInterval(this.pullInterval);
      this.pullInterval = null;
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleFocus);

    this.userId = null;
    this.userEmail = null;
    this.hasSyncAccess = false; // BUG-065 FIX: Reset sync access on cleanup
    this.initialized = false;
    this.pendingChanges = 0;
    this.lastError = null;

    // BUG-061: Reset sync racing prevention state
    this.fullSyncLock = false;
    this.syncQueuedAfterCurrent = false;
    this.lastSyncCompletedAt = 0;

    console.log('[SYNC] Manager cleaned up');
    this.notifyListeners();
  }

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  private handleVisibilityChange = (): void => {
    if (!this.settings.syncOnFocus) return;

    if (document.visibilityState === 'visible') {
      // Tab became visible - do full sync to get latest AND push any local changes
      // BUG-061: triggerFullSync handles cooldown/mutex, so this won't spam
      console.log('[SYNC] Tab became visible - requesting sync');
      this.triggerFullSync();
    } else if (document.visibilityState === 'hidden') {
      // Tab hidden - push any pending changes before user leaves
      // This catches: user adds data, then immediately switches to phone
      console.log('[SYNC] Tab hidden - pushing any pending changes');
      this.executePushIfNeeded();
    }
  };

  private handleFocus = (): void => {
    // BUG-061: Only trigger on focus if we haven't synced recently
    // The cooldown in triggerFullSync handles this, but we can skip the log noise
    if (!this.settings.syncOnFocus) return;

    // Skip if we just synced (visibility change likely already handled it)
    const timeSinceLastSync = Date.now() - this.lastSyncCompletedAt;
    if (this.lastSyncCompletedAt > 0 && timeSinceLastSync < SyncManager.SYNC_COOLDOWN_MS) {
      // Don't log - visibility change already logged
      return;
    }

    console.log('[SYNC] Window focused - requesting sync');
    this.triggerFullSync();
  };

  /**
   * Push immediately if there are pending changes or if we haven't pushed recently.
   * Used on tab hide to ensure data is synced before user switches devices.
   */
  private async executePushIfNeeded(): Promise<void> {
    // Clear any pending debounce timer
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
      this.pushTimer = null;
    }

    // Always push on hide to ensure data is available on other devices
    // This is lightweight if there are no changes (server handles upserts)
    if (this.userId && this.settings.autoSyncEnabled) {
      console.log('[SYNC] Pushing on tab hide');
      await this.executePush();
    }
  }

  // ---------------------------------------------------------------------------
  // Sync Operations
  // ---------------------------------------------------------------------------

  /**
   * Trigger debounced push after data mutation.
   * Multiple calls within debounce window are batched.
   */
  triggerPush(): void {
    if (!this.settings.autoSyncEnabled || !this.settings.syncOnMutation) {
      console.log('[SYNC] Push skipped - auto-sync disabled');
      return;
    }

    // BUG-065 FIX: Defense in depth - check both userId AND hasSyncAccess
    if (!this.userId || !this.hasSyncAccess) {
      console.log('[SYNC] Push skipped - no user or no sync access');
      return;
    }

    // Increment pending changes counter
    this.pendingChanges++;
    this.notifyListeners();

    // Clear existing timer
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
    }

    // Set new debounced timer
    this.pushTimer = setTimeout(async () => {
      console.log('[SYNC] Debounced push triggered');
      await this.executePush();
    }, this.settings.pushDebounceMs);

    console.log(`[SYNC] Push scheduled in ${this.settings.pushDebounceMs / 1000}s (${this.pendingChanges} pending)`);
  }

  /**
   * Trigger immediate pull (get latest from server).
   */
  async triggerPull(): Promise<void> {
    if (!this.settings.autoSyncEnabled) {
      return;
    }

    // BUG-065 FIX: Defense in depth - check both userId AND hasSyncAccess
    if (!this.userId || !this.hasSyncAccess) {
      return;
    }

    if (this.isSyncing) {
      console.log('[SYNC] Pull skipped - sync in progress');
      return;
    }

    await this.executePull();
  }

  /**
   * Trigger full sync (push then pull).
   * Called on login, window focus, etc.
   *
   * BUG-061 FIX: Uses mutex lock and cooldown to prevent sync racing.
   * - If sync in progress, queues ONE sync to run after (not multiple)
   * - If just synced (within cooldown), skips entirely
   */
  async triggerFullSync(): Promise<void> {
    // BUG-065 FIX: Defense in depth - check both userId AND hasSyncAccess
    if (!this.userId || !this.hasSyncAccess) {
      console.log('[SYNC] Full sync skipped - no user or no sync access');
      return;
    }

    // Cooldown check: skip if we just synced recently (prevents window focus spam)
    const timeSinceLastSync = Date.now() - this.lastSyncCompletedAt;
    if (this.lastSyncCompletedAt > 0 && timeSinceLastSync < SyncManager.SYNC_COOLDOWN_MS) {
      console.log(`[SYNC] Full sync skipped - cooldown (${Math.round(timeSinceLastSync / 1000)}s since last sync)`);
      return;
    }

    // Mutex check: if sync already in progress, queue ONE more
    if (this.fullSyncLock) {
      if (!this.syncQueuedAfterCurrent) {
        console.log('[SYNC] Full sync queued - will run after current sync completes');
        this.syncQueuedAfterCurrent = true;
      } else {
        console.log('[SYNC] Full sync skipped - already queued');
      }
      return;
    }

    // Acquire lock and execute
    this.fullSyncLock = true;
    try {
      await this.executeFullSync();
    } finally {
      this.lastSyncCompletedAt = Date.now();
      this.fullSyncLock = false;

      // If another sync was queued while we were running, execute it now
      if (this.syncQueuedAfterCurrent) {
        this.syncQueuedAfterCurrent = false;
        console.log('[SYNC] Executing queued sync');
        // Use setTimeout to avoid stack buildup
        setTimeout(() => this.triggerFullSync(), 100);
      }
    }
  }

  /**
   * Called after successful login.
   * Triggers full sync if enabled.
   */
  async onLogin(): Promise<void> {
    if (this.settings.syncOnLogin && this.settings.autoSyncEnabled) {
      console.log('[SYNC] Login detected - triggering full sync');
      await this.triggerFullSync();
    }
  }

  /**
   * Force immediate push (bypass debounce).
   * For manual sync button.
   */
  async forcePush(): Promise<{ success: boolean; error?: string }> {
    // Clear pending debounce timer
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
      this.pushTimer = null;
    }

    return await this.executePush();
  }

  /**
   * Force immediate push for critical actions (delete, stop recurring).
   * Bypasses debounce and awaits completion.
   * Use this when changes MUST reach server before user switches devices.
   *
   * RECURRING-FIX: Issue 2 - Turn off recurring not syncing to other devices
   */
  async forceImmediatePush(): Promise<{ success: boolean; error?: string }> {
    // Clear any pending debounce timer
    if (this.pushTimer) {
      clearTimeout(this.pushTimer);
      this.pushTimer = null;
    }

    // Reset pending counter since we're pushing now
    this.pendingChanges = 0;

    // Execute push immediately
    const result = await this.executePush();
    
    // Update completed timestamp for cooldown tracking
    if (result.success) {
      this.lastSyncCompletedAt = Date.now();
    }
    
    return result;
  }

  /**
   * Force immediate pull.
   * For manual sync button.
   */
  async forcePull(): Promise<{ success: boolean; error?: string }> {
    return await this.executePull();
  }

  /**
   * Force immediate full sync.
   * For manual sync button.
   */
  async forceFullSync(): Promise<{ success: boolean; error?: string }> {
    return await this.executeFullSync();
  }

  // ---------------------------------------------------------------------------
  // Internal Execution
  // ---------------------------------------------------------------------------

  private async executePush(): Promise<{ success: boolean; error?: string }> {
    // BUG-065 FIX: Final guard - check access even if called from pending timer
    if (!this.userId || !this.hasSyncAccess) {
      console.log('[SYNC] executePush aborted - no user or no sync access');
      return { success: false, error: 'No sync access' };
    }

    this.isSyncing = true;
    this.notifyListeners();

    try {
      const result = await pushAll();

      if (result.success) {
        this.pendingChanges = 0;
        this.lastError = null;
        this.connectionStatus = 'connected';
      } else {
        this.lastError = result.error || 'Push failed';
        this.connectionStatus = 'disconnected';
      }

      return result;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Push failed';
      this.lastError = errorMsg;
      this.connectionStatus = 'disconnected';
      return { success: false, error: errorMsg };
    } finally {
      this.isSyncing = false;
      this.notifyListeners();
    }
  }

  private async executePull(): Promise<{ success: boolean; error?: string }> {
    // BUG-065 FIX: Final guard - check access even if called from pending timer
    if (!this.userId || !this.hasSyncAccess) {
      console.log('[SYNC] executePull aborted - no user or no sync access');
      return { success: false, error: 'No sync access' };
    }

    this.isSyncing = true;
    this.notifyListeners();

    try {
      const result = await pull();

      if (result.success) {
        this.lastError = null;
        this.connectionStatus = 'connected';
      } else {
        this.lastError = result.error || 'Pull failed';
        this.connectionStatus = 'disconnected';
      }

      return { success: result.success, error: result.error };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Pull failed';
      this.lastError = errorMsg;
      this.connectionStatus = 'disconnected';
      return { success: false, error: errorMsg };
    } finally {
      this.isSyncing = false;
      this.notifyListeners();
    }
  }

  private async executeFullSync(): Promise<{ success: boolean; error?: string }> {
    this.isSyncing = true;
    this.notifyListeners();

    try {
      const result = await fullSync();

      if (result.success) {
        this.pendingChanges = 0;
        this.lastError = null;
        this.connectionStatus = 'connected';
      } else {
        this.lastError = result.error || 'Full sync failed';
        this.connectionStatus = 'disconnected';
      }

      return result;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Full sync failed';
      this.lastError = errorMsg;
      this.connectionStatus = 'disconnected';
      return { success: false, error: errorMsg };
    } finally {
      this.isSyncing = false;
      this.notifyListeners();
    }
  }

  // ---------------------------------------------------------------------------
  // Connection Check
  // ---------------------------------------------------------------------------

  private async checkConnectionStatus(): Promise<void> {
    this.connectionStatus = 'checking';
    this.notifyListeners();

    const connected = await checkConnection();
    this.connectionStatus = connected ? 'connected' : 'disconnected';
    this.notifyListeners();
  }

  // ---------------------------------------------------------------------------
  // Settings Management
  // ---------------------------------------------------------------------------

  private loadSettings(): SyncSettings {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_SYNC_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('[SYNC] Failed to load settings:', error);
    }
    return { ...DEFAULT_SYNC_SETTINGS };
  }

  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch (error) {
      console.error('[SYNC] Failed to save settings:', error);
    }
  }

  /**
   * Update sync settings.
   * Used by DevPage settings panel.
   */
  updateSettings(newSettings: Partial<SyncSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();

    // Reinitialize with new settings
    if (this.initialized && this.userId) {
      this.cleanup();
      this.init(this.userId, this.userEmail);
    }

    console.log('[SYNC] Settings updated:', this.settings);
    this.notifyListeners();
  }

  /**
   * Get current settings.
   */
  getSettings(): SyncSettings {
    return { ...this.settings };
  }

  // ---------------------------------------------------------------------------
  // Status & Subscription
  // ---------------------------------------------------------------------------

  /**
   * Get current sync status.
   */
  getStatus(): SyncStatus {
    return {
      isSyncing: this.isSyncing,
      lastSyncAt: getLastSyncAt(),
      pendingChanges: this.pendingChanges,
      lastError: this.lastError,
      connectionStatus: this.connectionStatus,
      autoSyncEnabled: this.settings.autoSyncEnabled,
    };
  }

  /**
   * Subscribe to status changes.
   * Returns unsubscribe function.
   */
  subscribe(listener: SyncStatusListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current status
    listener(this.getStatus());
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    const status = this.getStatus();
    this.listeners.forEach((listener) => listener(status));
  }

  // ---------------------------------------------------------------------------
  // Debug
  // ---------------------------------------------------------------------------

  /**
   * Get debug info for troubleshooting.
   */
  getDebugInfo(): Record<string, unknown> {
    return {
      initialized: this.initialized,
      userId: this.userId ? this.userId.slice(0, 8) + '...' : null,
      userEmail: this.userEmail,
      settings: this.settings,
      status: this.getStatus(),
      hasPushTimer: !!this.pushTimer,
      hasPullInterval: !!this.pullInterval,
    };
  }
}

// =============================================================================
// Singleton Export
// =============================================================================

export const syncManager = new SyncManager();

export default syncManager;
